/**
 * Who sees the staff consoles — **a hint, not a gate.**
 *
 * The API is the gate: every staff capability checks its scopes server-side, and staff
 * scopes come only from Cognito groups (`GROUP_SCOPES`, `apps/api/src/principal.ts`). The
 * console's pages are blank and call no capability, so all this decides is what to draw.
 * It reads the access token's `cognito:groups` without verifying it, which is fine for a
 * hint and would be wrong for anything else.
 *
 * Kept free of the DOM and of `config.ts`, so the API's drift spec can import it and fail
 * when a staff group is added on one side only.
 */

/** The Cognito groups that grant a `staff:*` scope. `publisher` is a machine identity, not staff. */
export const STAFF_GROUPS: readonly string[] = ['ops', 'finance', 'moderation', 'support'];

/** `cognito:groups` as JSON (`["ops"]`) or as the gateway flattens it (`[ops finance]`). */
export function staffGroupsOf(claims: Record<string, unknown> | undefined): string[] {
  const value = claims?.['cognito:groups'];
  const groups = Array.isArray(value)
    ? value.map(String)
    : typeof value === 'string'
      ? value
          .replace(/^\[|\]$/g, '')
          .split(/[\s,]+/)
          .filter((item) => item.length > 0)
      : [];
  return groups.filter((group) => STAFF_GROUPS.includes(group));
}

export type AdminAccess = 'signedOut' | 'notStaff' | 'envOff' | 'open';

/**
 * What `/admin` and `/seller/dashboard` show. `teamTools` is `teamToolsEnabled()`: never
 * in production until the console is accepted, and never where the environment does not
 * name itself. It comes first, so in production the route is closed for everybody, the
 * way the import harness's is.
 *
 * **The seller dashboard reuses this unchanged, on purpose** (D46 addendum, 2026-10-12).
 * It is staff tooling served at a seller-shaped path, and **this gate — not the prefix —
 * is what holds O32**. Giving it its own function would be two places to widen the day
 * somebody adds a staff group.
 */
export function adminAccess({
  teamTools,
  claims,
}: {
  teamTools: boolean;
  claims: Record<string, unknown> | undefined;
}): AdminAccess {
  if (!teamTools) return 'envOff';
  if (!claims) return 'signedOut';
  if (staffGroupsOf(claims).length === 0) return 'notStaff';
  return 'open';
}

/** The routes the staff console gate serves: the admin console, and the seller dashboard. */
const CONSOLE_ROUTES: readonly string[] = ['admin', 'sellerDash'];

/**
 * Whether a route is a console's and served. Where team tools are off an `/admin` or
 * `/seller/dashboard` path is closed, not gated: `App.tsx` serves the feed in its place,
 * as it does for the import harness, so production never draws a console panel. Takes the
 * route's name only, so this file stays free of the router.
 */
export function adminRouteOpen(route: { name: string }, teamTools: boolean): boolean {
  return CONSOLE_ROUTES.includes(route.name) && teamTools;
}

/**
 * Whether `App.tsx` draws the feed in the main column.
 *
 * **A predicate rather than an inline condition because this is the fail-closed path.**
 * A console route whose team tools are off must fall *through* to the feed; before the
 * seller dashboard was added to it, such a route rendered the ordinary shell with an
 * empty main column instead. Not an exposure — the lazy chunk never loads — but it is
 * the behaviour this gate claims, so it is asserted rather than described. (`apps/web`
 * has no DOM test harness, and adding one is a dependency conversation; a pure predicate
 * is the testable form of the same rule.)
 */
export function showsFeed(route: { name: string }, teamTools: boolean): boolean {
  if (route.name === 'feed') return true;
  if (route.name === 'importHarness') return !teamTools;
  return CONSOLE_ROUTES.includes(route.name) && !adminRouteOpen(route, teamTools);
}
